import {
  type MediaCardType,
  mediaStrings,
} from '@wandb/weave/common/types/media';
import {RemoveAction} from '@wandb/weave/components/Tag';
import {RemovableAlias} from '@wandb/weave/components/Tag/Alias';
import React from 'react';
import {components, type MultiValueProps} from 'react-select';

/** Shared between the key-info and metric-search media key pickers. */
export type MediaKeyOption = {
  value: string | undefined;
  displayText: string;
  /**
   * Card type of this key, when the option came from a metric search result.
   * Lets the picker learn the selected type from the click instead of looking
   * it up again. Absent on the legacy path and on stickied selections.
   */
  cardType?: MediaCardType;
};

export const MediaKeyMultiValue = (
  props: MultiValueProps<MediaKeyOption, true>
) => {
  return (
    <components.MultiValue
      {...props}
      components={{
        Container: () => (
          <RemovableAlias
            color="moon"
            label={props.data.displayText}
            removeAction={
              <RemoveAction
                {...props.removeProps}
                data-test="remove-media-key"
              />
            }
            truncatedPart="middle"
          />
        ),
        Label: () => null,
        Remove: () => null,
      }}
    />
  );
};

export const MEDIA_KEY_RESULT_LIMIT = 100;

export const SLIDER_KEY_TYPES = ['number'];
export const SLIDER_KEY_RESULT_LIMIT = 100;

/**
 * Sentinel value for the "results truncated" hint row. Mirrors
 * `MetricSearchDropdown`, which the line plot and media popup pickers use: the
 * menu doesn't page on scroll, so when the server has more results than the
 * requested page we say so rather than silently showing a prefix of the keys.
 * Rendered as a disabled option (`isResultLimitHintOption` feeds react-select's
 * `isOptionDisabled`) so it can't be selected.
 */
const RESULT_LIMIT_HINT_VALUE = '__media_metric_search_result_limit';

/** Option shape shared by the media key and slider key pickers. */
type KeySelectOption = {value: string | undefined; displayText: string};

export const resultLimitHintOption = (
  resultLimit: number
): KeySelectOption => ({
  value: RESULT_LIMIT_HINT_VALUE,
  displayText: `Limited to ${resultLimit} items. Refine search to see other options.`,
});

export const isResultLimitHintOption = (option: KeySelectOption): boolean =>
  option.value === RESULT_LIMIT_HINT_VALUE;
